import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Calendar, ArrowRight } from 'lucide-react';
import { fetchCollection } from '../../lib/cms';
import SEO from '../../components/SEO';

export default function TilbehoerEn() {
  const [tilbehoerList, setTilbehoerList] = useState([]);

  useEffect(() => {
    async function loadData() {
      const data = await fetchCollection('tilbehoer', 'en');
      setTilbehoerList(data);
    }
    loadData();
  }, []);

  return (
    <div className="relative min-h-screen bg-slate-50 text-slate-900 antialiased">
      <SEO
        title="Original Accessories for Danshaker"
        description="Browse original accessories and equipment for your Danshaker stall sorting machine. Perfectly designed for easy retrofitting."
        path="/en/accessories"
        lang="en"
      />

      {/* Background gradients */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(14,103,162,0.05),transparent_60%)]" />

      {/* Header Section */}
      <section className="mx-auto max-w-7xl px-4 pt-16 pb-12 md:px-8 md:pt-24">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#0E67A2]/10 px-4 py-1.5 text-xs font-bold text-[#0E67A2]">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            <span>Upgrade your machine</span>
          </div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            Original Accessories
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            All our accessories are custom designed to fit your Danshaker seamlessly and can be easily retrofitted to suit your stable routine.
          </p>
          <p className="text-xs text-slate-500 font-semibold uppercase tracking-widest">
            All prices excl. VAT in Euro (€)
          </p>
        </div>
      </section>

      {/* Accessories Grid */}
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-8">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {tilbehoerList.map((item, idx) => (
            <div
              key={idx}
              className="flex flex-col justify-between overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-md transition duration-300 hover:border-[#0E67A2]/40 hover:shadow-xl"
            >
              <div>
                <div className="aspect-4/3 w-full overflow-hidden bg-slate-100">
                  <img
                    src={item.image && item.image[0] ? item.image[0] : 'https://assets.auxodev.dk/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/danshaker-3-farver.png'}
                    alt={item.name}
                    width="400"
                    height="300"
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition duration-300 hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-baseline justify-between gap-2 mb-2">
                    <h3 className="text-xl font-bold text-slate-900">{item.name}</h3>
                    <span className="text-lg font-black text-[#0E67A2] whitespace-nowrap">{item.price}</span>
                  </div>
                  <p className="text-sm text-slate-600 font-medium leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
              <div className="p-6 pt-0">
                <Link
                  to="/en/contact"
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-100 py-3 text-sm font-bold text-slate-800 transition hover:bg-[#0E67A2] hover:text-white"
                >
                  Order / Inquire
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Box */}
      <section className="mx-auto max-w-7xl px-4 pb-24 md:px-8">
        <div className="rounded-3xl bg-gradient-to-r from-slate-900 to-slate-800 p-8 md:p-12 text-white shadow-2xl flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="space-y-3 max-w-xl text-center md:text-left">
            <h2 className="text-2xl md:text-3xl font-extrabold">Do you have questions about equipment or fit?</h2>
            <p className="text-slate-300 text-sm md:text-base leading-relaxed">
              We are ready to guide you on which accessories best support your daily stable routine and bedding type.
            </p>
          </div>
          <div className="flex flex-wrap gap-4 shrink-0">
            <Link
              to="/en/contact"
              className="rounded-xl bg-[#0E67A2] px-6 py-3.5 font-bold text-white transition hover:bg-[#0E67A2]/90 shadow-lg"
            >
              Contact customer support
            </Link>
            <Link
              to="/en/free-demo"
              className="flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-6 py-3.5 font-bold text-white transition hover:bg-white/20"
            >
              <Calendar className="h-4 w-4" aria-hidden="true" />
              Book free demo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
